import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { t, archetypeLabel } from "../lib/i18n";
import { toast } from "sonner";
import { User, Sword, Trophy, Plus, X } from "@phosphor-icons/react";

export default function MyProfile() {
  const { user } = useAuth();
  const [tab, setTab] = useState("profile");

  if (user === null) return <div className="p-10 font-mono text-white/40">{t.loading}</div>;
  if (!user || user.role !== "player") return <Navigate to="/login" replace />;

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12">
      <div className="border-b border-white/10 pb-6 flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF0055] font-mono">
            // {user.email}
          </div>
          <h1 className="font-display font-black uppercase italic text-5xl mt-2">
            {t.myAccountTitle}
          </h1>
        </div>
      </div>

      <div className="border-b border-white/10 flex gap-1 mt-6 overflow-x-auto">
        <TabBtn id="profile" label={t.myProfileTitle} icon={User} active={tab} setTab={setTab} />
        <TabBtn id="decks" label={t.myDecksTitle} icon={Sword} active={tab} setTab={setTab} />
        <TabBtn id="decksets" label={t.myDeckSetsTitle} icon={Sword} active={tab} setTab={setTab} />
        <TabBtn id="tournaments" label={t.myTournaments} icon={Trophy} active={tab} setTab={setTab} />
      </div>

      <div className="mt-8">
        {tab === "profile" && <ProfileTab />}
        {tab === "decks" && <DecksTab />}
        {tab === "decksets" && <DeckSetsTab />}
        {tab === "tournaments" && <TournamentsTab playerId={user.player_id} />}
      </div>
    </div>
  );
}

function TabBtn({ id, label, icon: Icon, active, setTab }) {
  return (
    <button
      onClick={() => setTab(id)}
      data-testid={`my-tab-${id}`}
      className={`flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.25em] font-bold whitespace-nowrap border-b-2 -mb-px ${
        active === id ? "text-[#00E5FF] border-[#00E5FF]" : "text-white/50 border-transparent hover:text-white"
      }`}
    >
      <Icon size={14} /> {label}
    </button>
  );
}

function ProfileTab() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/me").then((r) => setProfile(r.data.player)).catch(() => {});
  }, []);

  if (!profile) return <div className="font-mono text-white/40">{t.loading}</div>;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/me/profile", {
        handle: profile.handle,
        real_name: profile.real_name,
        country: profile.country,
        bio: profile.bio,
        avatar_url: profile.avatar_url,
      });
      setProfile(data);
      toast.success(t.profileUpdated);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t.failed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid md:grid-cols-3 gap-6 max-w-3xl">
      <div className="md:col-span-2 space-y-4">
        <Input label={t.handle} value={profile.handle || ""} onChange={(v) => setProfile({ ...profile, handle: v })} testid="prof-handle" />
        <Input label={t.realName} value={profile.real_name || ""} onChange={(v) => setProfile({ ...profile, real_name: v })} />
        <Input label={t.country} value={profile.country || ""} onChange={(v) => setProfile({ ...profile, country: v.toUpperCase() })} />
        <div>
          <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 block mb-1">{t.bio}</label>
          <textarea rows={3} value={profile.bio || ""} onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
            className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]" />
        </div>
        <button onClick={save} disabled={busy} data-testid="save-profile-btn"
          className="px-5 py-2.5 bg-[#FF0055] hover:bg-[#FF3377] text-white font-bold uppercase tracking-[0.25em] text-xs disabled:opacity-50">
          {busy ? t.creating : t.saveProfile}
        </button>
        <p className="text-[10px] text-white/30 font-mono">{user.email} · {t.emailNotEditable}</p>
      </div>
      <div className="border border-white/10 bg-[#0C0C14] p-6">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40">{t.points}</div>
        <div className="font-display font-black italic text-5xl text-[#E2FF00] mt-2">{profile.points}</div>
        <div className="grid grid-cols-2 gap-3 mt-5 text-sm font-mono">
          <div><div className="text-[10px] text-white/40 uppercase">{t.wins}</div><div className="text-[#00E5FF] font-bold text-2xl">{profile.wins}</div></div>
          <div><div className="text-[10px] text-white/40 uppercase">{t.losses}</div><div className="text-white/70 font-bold text-2xl">{profile.losses}</div></div>
        </div>
      </div>
    </div>
  );
}

function DecksTab() {
  const [decks, setDecks] = useState([]);

  const refresh = () => {
    api.get("/me/decks").then((r) => setDecks(r.data));
  };

  useEffect(() => {
    refresh();
  }, []);

  const remove = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    await api.delete(`/me/decks/${id}`);
    toast.success(t.deleted);
    refresh();
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3">
        <Link to="/deck-builder" data-testid="new-my-deck-btn"
          className="inline-block px-5 py-2.5 bg-[#FF0055] hover:bg-[#FF3377] text-white font-bold uppercase tracking-[0.25em] text-xs">
          {t.newDeck}
        </Link>
        <Link to="/deck-builder?beys=6" data-testid="new-pokeblade-deck-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#9B7AFF]/50 text-[#9B7AFF] hover:bg-[#9B7AFF]/10 font-bold uppercase tracking-[0.25em] text-xs">
          + Pokeblade · 6 Beyów
        </Link>
        <Link to="/deck-builder?beys=4" data-testid="new-beypong-deck-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#E2FF00]/50 text-[#E2FF00] hover:bg-[#E2FF00]/10 font-bold uppercase tracking-[0.25em] text-xs">
          + BEY-PONG · 4 Beye
        </Link>
      </div>

      {decks.length === 0 ? (
        <div className="text-center text-white/40 py-20 font-mono text-sm">{t.noDecksYet}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {decks.map((d) => {
            const count = (d.beys || []).length || (d.blades || []).length || 3;
            const kind = count === 6 ? "POKEBLADE" : count === 4 ? "BEY-PONG" : "STANDARD";
            const accent = count === 6 ? "text-[#9B7AFF]" : count === 4 ? "text-[#E2FF00]" : "text-[#FF0055]";
            return (
            <div key={d.id} data-testid={`my-deck-${d.id}`} className="border border-white/10 bg-[#0C0C14] p-5 hover:border-[#FF0055]/60 group">
              <div className="flex items-center justify-between">
                <div className={`text-[10px] uppercase tracking-[0.25em] font-bold ${accent}`}>
                  {archetypeLabel(d.archetype)}
                </div>
                <span
                  data-testid={`my-deck-${d.id}-kind`}
                  className={`text-[9px] uppercase tracking-[0.25em] font-mono font-bold border px-1.5 py-0.5 ${accent} border-current`}
                >
                  {kind} · {count}
                </span>
              </div>
              <div className="font-display font-black uppercase italic text-2xl mt-2">{d.name}</div>
              <p className="text-xs text-white/50 mt-2 line-clamp-2">{d.description}</p>
              <div className="flex gap-3 mt-4 text-[10px] uppercase tracking-[0.25em] font-bold">
                <Link to={`/decks/${d.id}`} className="text-white/70 hover:text-[#00E5FF]">{t.viewAll.replace(t.viewAll, "View")}</Link>
                <Link to={`/deck-builder/${d.id}${count === 6 ? "?beys=6" : count === 4 ? "?beys=4" : ""}`} className="text-white/70 hover:text-[#00E5FF]">{t.edit}</Link>
                <button onClick={() => remove(d.id)} className="text-[#FF0055] ml-auto">{t.delete}</button>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DeckSetsTab() {
  const [sets, setSets] = useState([]);
  const [decks, setDecks] = useState([]);
  const [form, setForm] = useState(null);
  const empty = { name: "", description: "", deck_ids: [] };

  const refresh = () => api.get("/me/decksets").then((r) => setSets(r.data));
  useEffect(() => {
    refresh();
    api.get("/me/decks").then((r) => setDecks(r.data));
  }, []);

  const save = async () => {
    try {
      if (form.id) await api.put(`/me/decksets/${form.id}`, form);
      else await api.post("/me/decksets", form);
      toast.success(t.saved);
      setForm(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t.failed);
    }
  };
  const remove = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    await api.delete(`/me/decksets/${id}`);
    toast.success(t.deleted);
    refresh();
  };

  const toggleDeck = (id) => {
    setForm((f) => {
      const has = f.deck_ids.includes(id);
      return { ...f, deck_ids: has ? f.deck_ids.filter((x) => x !== id) : [...f.deck_ids, id] };
    });
  };

  return (
    <div>
      <button onClick={() => setForm(empty)} data-testid="new-deckset-btn"
        className="px-5 py-2.5 bg-[#FF0055] hover:bg-[#FF3377] text-white font-bold uppercase tracking-[0.25em] text-xs">
        {t.newDeckSet}
      </button>
      {sets.length === 0 ? (
        <div className="text-center text-white/40 py-20 font-mono text-sm">{t.noDeckSetsYet}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {sets.map((s) => {
            const setDecks = s.deck_ids.map((id) => decks.find((d) => d.id === id)).filter(Boolean);
            return (
              <div key={s.id} data-testid={`deckset-${s.id}`} className="border border-white/10 bg-[#0C0C14] p-5">
                <div className="font-display font-black uppercase italic text-2xl">{s.name}</div>
                <p className="text-xs text-white/50 mt-2 line-clamp-2">{s.description}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {setDecks.length === 0 && <span className="text-[10px] text-white/30 font-mono">{t.none}</span>}
                  {setDecks.map((d) => (
                    <span key={d.id} className="text-[10px] uppercase tracking-[0.2em] font-bold border border-white/15 text-white/70 px-2 py-1">
                      {d.name}
                    </span>
                  ))}
                </div>
                <div className="flex gap-3 mt-4 text-[10px] uppercase tracking-[0.25em] font-bold">
                  <button onClick={() => setForm(s)} className="text-white/70 hover:text-[#00E5FF]">{t.edit}</button>
                  <button onClick={() => remove(s.id)} className="text-[#FF0055] ml-auto">{t.delete}</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {form && (
        <Modal title={form.id ? t.editDeckSet : t.newDeckSet} onClose={() => setForm(null)}>
          <div className="space-y-3">
            <Input label={t.deckSetName} value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="set-name-input" />
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 block mb-1">{t.deckSetDescription}</label>
              <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 block mb-2">{t.selectDecks}</label>
              {decks.length === 0 ? (
                <p className="text-xs text-white/40 font-mono">{t.noDecksYet}</p>
              ) : (
                <div className="grid grid-cols-2 gap-1.5 max-h-60 overflow-y-auto border border-white/10 p-2">
                  {decks.map((d) => {
                    const checked = form.deck_ids.includes(d.id);
                    return (
                      <label key={d.id} data-testid={`set-deck-${d.id}`} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-white/5 px-2 py-1">
                        <input type="checkbox" checked={checked} onChange={() => toggleDeck(d.id)} />
                        <span>{d.name} <span className="text-white/40">({d.archetype})</span></span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setForm(null)} className="px-4 py-2 text-xs uppercase tracking-[0.25em] font-bold text-white/60">{t.cancel}</button>
            <button onClick={save} data-testid="save-deckset-btn"
              className="px-5 py-2 bg-[#FF0055] hover:bg-[#FF3377] text-white font-bold uppercase tracking-[0.25em] text-xs">{t.save}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function TournamentsTab({ playerId }) {
  const [tournaments, setTournaments] = useState([]);
  useEffect(() => {
    api.get("/tournaments").then((r) => setTournaments(r.data.filter((tt) => (tt.player_ids || []).includes(playerId))));
  }, [playerId]);

  if (tournaments.length === 0) {
    return <div className="text-center text-white/40 py-20 font-mono text-sm">{t.none}</div>;
  }
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {tournaments.map((tt) => (
        <Link key={tt.id} to={`/tournaments/${tt.id}`} data-testid={`my-tournament-${tt.id}`}
          className="border border-white/10 bg-[#0C0C14] p-5 hover:border-[#FF0055]/60">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF0055]">{tt.status}</div>
          <div className="font-display font-black uppercase italic text-2xl mt-2">{tt.name}</div>
          <div className="text-xs text-white/50 mt-1 font-mono">{tt.location}</div>
        </Link>
      ))}
    </div>
  );
}

function Input({ label, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 block mb-1">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]"
      />
    </div>
  );
}

function Select({ label, value, options, onChange }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40">{label}</label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border border-white/15 px-2 py-2 mt-1 text-xs focus:outline-none focus:border-[#00E5FF]">
        <option className="bg-[#0C0C14]" value="">—</option>
        {options.map((o) => (
          <option key={o.id} className="bg-[#0C0C14]" value={o.id}>{o.name}</option>
        ))}
      </select>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0C0C14] border border-white/15 max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center pb-3 border-b border-white/10">
          <h3 className="font-display font-black uppercase italic text-2xl">{title}</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white text-2xl leading-none">×</button>
        </div>
        <div className="mt-5">{children}</div>
      </div>
    </div>
  );
}
